<app-ssc-shell title="AI Translation Studio">
  <section class="translation-studio">
    <div class="translation-main">
      <section class="translation-hero">
        <div>
          <span class="ssc-badge">Human-in-the-loop workspace</span>
          <h2>Question Translation Studio</h2>
        </div>
        <div class="translation-hero__pair">
          <span>Current Pair</span>
          <strong>{{ languagePairLabel }}</strong>
        </div>
      </section>

      <section class="translation-composer ssc-card">
        <div class="translation-composer__toolbar">
          <label class="ssc-field">
            <span>Source Language</span>
            <div class="translation-autocomplete">
              <input
                type="text"
                [ngModel]="sourceLanguageSearch"
                (ngModelChange)="onSourceLanguageSearch($event)"
                (focus)="sourceLanguagePanelOpen = true"
                (blur)="closeAutocompletePanel('source')"
                placeholder="Auto-detect if blank"
                autocomplete="off"
              >
              @if (sourceLanguagePanelOpen) {
                <div class="translation-autocomplete__panel">
                  @if (languagesLoading) {
                    <button type="button" disabled>Loading languages...</button>
                  } @else if (!filteredSourceLanguages.length) {
                    <button type="button" disabled>No language found</button>
                  }
                  @for (language of filteredSourceLanguages; track language.language_code) {
                    <button type="button" (mousedown)="selectSourceLanguage(language)">
                      {{ language.language_name }} @if (language.native_name) { - {{ language.native_name }} }
                    </button>
                  }
                </div>
              }
            </div>
          </label>

          <label class="ssc-field">
            <span>Target Language</span>
            <div class="translation-autocomplete">
              <div class="translation-multiselect">
                @for (language of selectedTargetLanguages; track language.language_code) {
                  <button type="button" (click)="removeTargetLanguage(language.language_code)">{{ language.language_name }} x</button>
                }
                <input
                  type="text"
                  [ngModel]="targetLanguageSearch"
                  (ngModelChange)="onTargetLanguageSearch($event)"
                  (focus)="targetLanguagePanelOpen = true"
                  (blur)="closeAutocompletePanel('target')"
                  placeholder="Search target language"
                  autocomplete="off"
                >
              </div>
              @if (targetLanguagePanelOpen) {
                <div class="translation-autocomplete__panel">
                  @if (languagesLoading) {
                    <button type="button" disabled>Loading languages...</button>
                  } @else if (!filteredTargetLanguages.length) {
                    <button type="button" disabled>No language found</button>
                  }
                  @for (language of filteredTargetLanguages; track language.language_code) {
                    <button type="button" (mousedown)="selectTargetLanguage(language)">
                      {{ language.language_name }} @if (language.native_name) { - {{ language.native_name }} }
                    </button>
                  }
                </div>
              }
            </div>
          </label>

          <label class="ssc-field">
            <span>Content Type</span>
            <div class="translation-autocomplete">
              <input
                type="text"
                [ngModel]="subjectSearch"
                (ngModelChange)="onSubjectSearch($event)"
                (focus)="subjectPanelOpen = true"
                (blur)="closeAutocompletePanel('subject')"
                placeholder="Optional content type"
                autocomplete="off"
              >
              @if (subjectPanelOpen) {
                <div class="translation-autocomplete__panel">
                  @if (subjectsLoading) {
                    <button type="button" disabled>Loading subjects...</button>
                  } @else if (!filteredSubjects.length) {
                    <button type="button" disabled>No subject found</button>
                  }
                  @for (item of filteredSubjects; track item.subject_code) {
                    <button type="button" (mousedown)="selectSubject(item)">{{ item.subject_name }}</button>
                  }
                </div>
              }
            </div>
          </label>
        </div>

        <label class="ssc-field translation-question">
          <span>Examination Question</span>
          <textarea [(ngModel)]="questionText" (paste)="preserveQuestionPaste($event)" placeholder="Type/paste question text, or paste a screenshot here"></textarea>
        </label>

        <div class="translation-image-row">
          <label class="translation-image-upload">
            <input type="file" accept="image/png,image/jpeg,image/webp" (change)="onImageSelected($event)">
            <strong>Attach question image</strong>
            <span>PNG, JPG, JPEG, or WEBP. You can also paste a screenshot in the question box.</span>
          </label>
          @if (attachedImage) {
            <div class="translation-image-preview">
              <img [src]="attachedImagePreview" alt="Attached question screenshot">
              <div>
                <strong>{{ attachedImage.name }}</strong>
                <button class="ssc-btn tiny" type="button" (click)="removeAttachedImage()">Remove</button>
              </div>
            </div>
          }
        </div>

        <div class="translation-composer__footer">
          <div class="ssc-pill-row">
            <span class="ssc-badge">{{ selectedSourceLanguage?.language_name || 'Auto-detect source' }}</span>
            <span class="ssc-badge">{{ selectedSubject?.subject_name || 'Subject optional' }}</span>
            @for (language of selectedTargetLanguages; track language.language_code) {
              <span class="ssc-badge">{{ language.language_name }}</span>
            }
            <span class="ssc-badge">Option labels locked</span>
            <span class="ssc-badge">Formula preservation ON</span>
            <span class="ssc-badge">Tables/images check ON</span>
          </div>
          <div class="translation-actions">
            <button class="ssc-btn">Back-Translate</button>
            <button class="ssc-btn primary" type="button" [disabled]="translateLoading || (!questionText.trim() && !attachedImage)" (click)="translateQuestion()">
              @if (translateLoading) {
                <span class="translation-loader" aria-hidden="true"></span>
                Translating...
              } @else {
                Translate
              }
            </button>
          </div>
        </div>
        @if (translateError) {
          <div class="translation-error">{{ translateError }}</div>
        }
      </section>

      <article class="ssc-card translation-output">
        <div class="translation-section-head">
          <div>
            <h3 class="ssc-section-title">Translated Output</h3>
            <p>{{ selectedTargetLanguages.length }} target language(s)</p>
          </div>
          <span class="ssc-badge green">Draft ready</span>
        </div>
        <div class="ssc-question translation-output__text">
          @if (translateLoading) {
            <div class="translation-output-loading">
              <span class="translation-loader" aria-hidden="true"></span>
              Preparing translation...
            </div>
          } @else {
            {{ translatedText }}
          }
        </div>
        <div class="ssc-pill-row">
          <span class="ssc-badge green">Structure preserved</span>
          <span class="ssc-badge green">Formula preserved</span>
          <span class="ssc-badge green">Options preserved</span>
        </div>
      </article>

      @if (extractedImageText) {
        <article class="ssc-card translation-output">
          <div class="translation-section-head">
            <div>
              <h3 class="ssc-section-title">Extracted Image Text</h3>
              <p>Saved separately from typed question text</p>
            </div>
          </div>
          <div class="ssc-question translation-output__text compact">
            {{ extractedImageText }}
          </div>
        </article>
      }
    </div>

    <aside class="translation-side-panel">
      <div class="ssc-card">
        <h3 class="ssc-section-title">Per-question Quality</h3>
        <div class="ssc-metric"><span>Confidence</span><b>{{ translationQuality.confidence }}</b></div>
        <div class="ssc-metric"><span>Adequacy</span><b>{{ translationQuality.adequacy }}</b></div>
        <div class="ssc-metric"><span>Fluency</span><b>{{ translationQuality.fluency }}</b></div>
        <div class="ssc-metric"><span>Terminology</span><b>{{ translationQuality.terminology }}</b></div>
        <div class="ssc-metric"><span>Formatting</span><b>{{ translationQuality.formatting }}</b></div>
      </div>

      <div class="ssc-card">
        <h3 class="ssc-section-title">Special Content Checks</h3>
        <div class="translation-check"><input type="checkbox" checked> <span>Mathematical notation</span></div>
        <div class="translation-check"><input type="checkbox" checked> <span>Tables / structured content</span></div>
        <div class="translation-check"><input type="checkbox" checked> <span>Image text overlay</span></div>
        <div class="translation-check"><input type="checkbox" checked> <span>RTL / multi-script rendering</span></div>
      </div>

      <div class="ssc-card">
        <h3 class="ssc-section-title">Language Details</h3>
        <div class="ssc-metric"><span>Source</span><b>{{ selectedSourceLanguage?.language_name || 'Auto-detect' }}</b></div>
        <div class="ssc-metric"><span>Source Script</span><b>{{ selectedSourceLanguage?.script_name || 'Auto-detect' }}</b></div>
        <div class="ssc-metric"><span>Targets</span><b>{{ targetLanguageNames }}</b></div>
        <div class="ssc-metric"><span>Target Scripts</span><b>{{ targetLanguageScripts }}</b></div>
        <div class="ssc-metric"><span>Directions</span><b>{{ targetLanguageDirections }}</b></div>
        <div class="ssc-metric"><span>Subject</span><b>{{ selectedSubject?.subject_name || 'Optional' }}</b></div>
      </div>
    </aside>
  </section>
</app-ssc-shell>
